import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Toggle } from '@/components/ui/Toggle';
import { useCookieConsentStore } from '@/stores/cookie-consent-store';

/** Granular cookie preferences — opened from the banner or the "Cookie settings" launcher. */
export function CookiePreferencesModal() {
  const { t } = useTranslation();
  const isOpen = useCookieConsentStore((s) => s.isPreferencesModalOpen);
  const preferences = useCookieConsentStore((s) => s.preferences);
  const closePreferencesModal = useCookieConsentStore((s) => s.closePreferencesModal);
  const savePreferences = useCookieConsentStore((s) => s.savePreferences);
  const acceptAll = useCookieConsentStore((s) => s.acceptAll);

  const [analytics, setAnalytics] = useState(preferences?.analytics ?? false);
  const [marketing, setMarketing] = useState(preferences?.marketing ?? false);

  // Re-sync the draft toggles to the saved decision each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setAnalytics(preferences?.analytics ?? false);
      setMarketing(preferences?.marketing ?? false);
    }
  }, [isOpen, preferences]);

  return (
    <Modal
      open={isOpen}
      onClose={closePreferencesModal}
      title={t('cookieConsent.preferences.title')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={() => savePreferences({ analytics, marketing })}>
            {t('cookieConsent.preferences.save')}
          </Button>
          <Button variant="primary" className="text-white" onClick={acceptAll}>
            {t('cookieConsent.preferences.acceptAll')}
          </Button>
        </>
      }
    >
      <p className="text-[14px] leading-[18px] text-barita-ink/70">
        {t('cookieConsent.preferences.description')}
      </p>

      <div className="mt-6 flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[14px] font-semibold text-barita-ink">
              {t('cookieConsent.preferences.necessary.label')}
            </p>
            <p className="mt-1 text-[13px] leading-[17px] text-barita-ink/60">
              {t('cookieConsent.preferences.necessary.description')}
            </p>
          </div>
          <Badge variant="neutral" className="shrink-0">
            {t('cookieConsent.preferences.alwaysActive')}
          </Badge>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[14px] font-semibold text-barita-ink">
              {t('cookieConsent.preferences.analytics.label')}
            </p>
            <p className="mt-1 text-[13px] leading-[17px] text-barita-ink/60">
              {t('cookieConsent.preferences.analytics.description')}
            </p>
          </div>
          <Toggle
            checked={analytics}
            onChange={setAnalytics}
            label={t('cookieConsent.preferences.analytics.label')}
            className="mt-1 shrink-0"
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[14px] font-semibold text-barita-ink">
              {t('cookieConsent.preferences.marketing.label')}
            </p>
            <p className="mt-1 text-[13px] leading-[17px] text-barita-ink/60">
              {t('cookieConsent.preferences.marketing.description')}
            </p>
          </div>
          <Toggle
            checked={marketing}
            onChange={setMarketing}
            label={t('cookieConsent.preferences.marketing.label')}
            className="mt-1 shrink-0"
          />
        </div>
      </div>
    </Modal>
  );
}
